import type { Nullable } from "../types";

/**
 * The node types that only hold other nodes. A document made of nothing but these says nothing,
 * however many of them there are — an empty paragraph is what an untouched editor produces, and a
 * hard break is what pressing Enter in one produces.
 *
 * Anything else is content even with no text under it: a mention names a person, an image is the
 * message. That is why the walk below asks which types are structural rather than looking for text
 * nodes — a note that is one mention and nothing else has no text and is not blank.
 */
const STRUCTURAL_NODE_TYPES = new Set([
	"doc",
	"paragraph",
	"heading",
	"blockquote",
	"bulletList",
	"orderedList",
	"listItem",
	"taskList",
	"taskItem",
	"table",
	"tableRow",
	"tableCell",
	"tableHeader",
	"hardBreak",
]);

interface TiptapNodeLike {
	type?: string;
	text?: string;
	content?: TiptapNodeLike[];
}

/**
 * Whether a note's text says nothing at all.
 *
 * The two clients disagree about what an untouched editor is worth, which is the whole reason this
 * lives in one place. The web editor emits `""` when it goes empty; the native one always emits a
 * serialised document, so an untouched field arrives as a document holding one empty paragraph.
 * Both are blank, and a caller that only tested for `""` would let the second one through.
 *
 * @param note - The note's text as it is stored: a serialised Tiptap document, or plain text from
 *   a surface that never ran an editor.
 * @returns True when the note carries no words, no mention and no embedded node — the case in
 *   which the note needs an attachment to be worth writing.
 */
export function isNoteTextBlank(note: Nullable<string>): boolean {
	if (!note?.trim()) return true;

	const document = parseDocument(note);
	// Not a document at all: plain text, and it is not blank, because the trim above already said
	// so. A surface that stores its notes without an editor is served by that branch alone.
	if (!document) return false;

	return !hasContent(document);
}

/** The parsed document, or null when the string is not one — plain text, or malformed JSON. */
function parseDocument(note: string): TiptapNodeLike | null {
	try {
		const parsed = JSON.parse(note) as unknown;
		if (!parsed || typeof parsed !== "object") return null;
		return parsed as TiptapNodeLike;
	} catch {
		return null;
	}
}

function hasContent(node: TiptapNodeLike): boolean {
	if (node.type === "text") return !!node.text?.trim();
	if (node.type && !STRUCTURAL_NODE_TYPES.has(node.type)) return true;
	return (node.content ?? []).some(hasContent);
}
